/**
 * Unit Conversion Dialog
 * Shown when a user changes an ingredient's unit to a different group
 * (e.g., weight → count) where automatic conversion isn't possible.
 * The user enters a multiplier and sees a preview of how recipe quantities will scale.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UNIT_MAP, formatScaledQuantity, QUANTITY_MIN_POSITIVE } from '@/types/units';
import type { IngredientRecipeUsage } from '@/types/api';

interface UnitConversionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  oldUnit: string;
  newUnit: string;
  recipeUsages: IngredientRecipeUsage[];
  onConfirm: (multiplier: number) => void;
  onCancel: () => void;
}

export function UnitConversionDialog({
  open,
  onOpenChange,
  oldUnit,
  newUnit,
  recipeUsages: allUsages,
  onConfirm,
  onCancel,
}: UnitConversionDialogProps) {
  const [multiplierInput, setMultiplierInput] = useState('');
  // The server converts only rows still in the old unit; rows already in another unit are left as they are.
  const recipeUsages = allUsages.filter((usage) => usage.unit === oldUnit);
  const unchangedByUnit = new Map<string, number>();
  for (const usage of allUsages) {
    if (usage.unit !== oldUnit) unchangedByUnit.set(usage.unit, (unchangedByUnit.get(usage.unit) ?? 0) + 1);
  }
  const unchangedNote = [...unchangedByUnit.entries()]
    .map(([unit, count]) => `${count} recipe${count === 1 ? '' : 's'} already use${count === 1 ? 's' : ''} ${unit}`)
    .join(', ');

  // Reset multiplier input whenever the dialog opens/closes
  useEffect(() => {
    if (!open) setMultiplierInput('');
  }, [open]);

  const multiplier = parseFloat(multiplierInput);
  const isValid = !isNaN(multiplier) && multiplier > 0;

  const oldLabel = UNIT_MAP.get(oldUnit)?.label ?? oldUnit;
  const newLabel = UNIT_MAP.get(newUnit)?.label ?? newUnit;

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(multiplier);
      setMultiplierInput('');
    }
  };

  const handleCancel = () => {
    setMultiplierInput('');
    onCancel();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleCancel();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unit Conversion Required</DialogTitle>
          <DialogDescription>
            Changing from <strong>{oldLabel}</strong> to <strong>{newLabel}</strong> requires
            a conversion multiplier. Enter how many {newLabel.toLowerCase()} equal one {oldLabel.toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conversion-multiplier">
              Conversion multiplier (1 {oldUnit} = ? {newUnit})
            </Label>
            <Input
              id="conversion-multiplier"
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              placeholder="e.g., 10"
              value={multiplierInput}
              onChange={(e) => setMultiplierInput(e.target.value)}
              autoFocus
            />
          </div>

          {unchangedNote ? (
            <p className="text-xs text-muted-foreground">
              {recipeUsages.length === 0
                ? `No recipe is in ${oldUnit}, so no recipe quantities will change. ${unchangedNote}.`
                : `${unchangedNote} and will not change.`}
            </p>
          ) : null}

          {recipeUsages.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Preview — recipe quantity changes</Label>
              <div className="max-h-48 overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Recipe</th>
                      <th className="px-3 py-2 text-right font-medium">Current</th>
                      <th className="px-3 py-2 text-right font-medium">New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipeUsages.map((usage) => {
                      const currentQty = usage.quantityPerLarge ? parseFloat(usage.quantityPerLarge) : null;
                      const rawScaled = currentQty !== null && isValid ? currentQty * multiplier : null;
                      const isClamped = rawScaled !== null && rawScaled > 0 && rawScaled < QUANTITY_MIN_POSITIVE;
                      const displayQty = rawScaled !== null
                        ? formatScaledQuantity(isClamped ? QUANTITY_MIN_POSITIVE : rawScaled)
                        : '—';

                      return (
                        <tr key={usage.recipeId} className="border-t">
                          <td className="px-3 py-1.5 truncate max-w-[180px]" title={usage.recipeName}>
                            {usage.recipeName}
                          </td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">
                            {currentQty !== null ? `${currentQty} ${oldUnit}` : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right font-medium">
                            {currentQty !== null ? (
                              <>
                                {displayQty} {newUnit}
                                {isClamped && (
                                  <span className="ml-1 text-amber-600 text-xs" title="Clamped to minimum precision">
                                    (min)
                                  </span>
                                )}
                              </>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!isValid}>
            Apply Conversion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
